<template>
  <TableTemplate
    v-model="mappeDefaultProductsFields"

    :columns="tableColumns"
    :columnVariables="tableColumnVariables"
    :capitalize="capitalize"
    :skeletonClass="skeletonClass"

    :newlyCreatedAlertKey="newlyCreatedAlertKey"
    :page="page"
    :max="max"
    :selected="selected"
    :fieldsWithCommas="fieldsWithCommas"
    :extendedSlots="extendedSlots"

    :remove-cursor='true'
    :remove-row-click="true"
    :enable-sorting="true"
    :hidePagination="hidePagination"
    :remove-card="removeCard"
    :raw-table="rawTable"
    :removeHeader="removeHeader"
    @update-column-order="updateColumnOrder"

    @set-page="v => page = v"

    ref="table"
  >
    <template #new-item-alert-content="props">
      <slot name="new-item-alert-content" v-bind="props"></slot>
    </template>

    <template #above>
      <TableHeader class="TableHeader"
        v-model="selecting"

        :title="computedTitle"
        :showButton="showButton"
        :refetch="refetch"
        :alwaysShowMultiSelectCheckbox="true"
        :mobile-responsive-header="true"
        :exportParams="exportEndpointParams"
        select-multiple-value="Producten selecteren"

        csv-name="products"
        export-endpoint="/api/products/export"
        @setting-layout="customizeSetting"
        @customize-layout="customizeLayout"
      >
        <template #after-title>
          <slot name="after-title"></slot>
        </template>

        <template #header-buttons-before="props">
          <slot name="header-buttons-before" v-bind="props"></slot>

          <div leave-class="d-flex align-items-center">
            <button class="btn btn-secondary filter-button mr-2" @click="showFilters = !showFilters">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 48 48">
                <g transform="matrix(3.4285714285714284,0,0,3.4285714285714284,0,0)"><path d="M13.5.5H.5a6.51,6.51,0,0,0,5,6.33V13.5l3-2V6.83A6.51,6.51,0,0,0,13.5.5Z" style="fill: none;stroke: currentColor;stroke-linecap: round;stroke-linejoin: round"></path></g>
              </svg>

              {{ !showFilters ? "Filters tonen" : "Verberg filters" }}
            </button>
          </div>
        </template>

        <template #header-buttons-after="props">
          <slot name="header-buttons-after" v-bind="props"></slot>
        </template>
      </TableHeader>

      <transition
        @enter="enterTrans"
        @leave="leaveTrans"
      >
        <div v-if="showFilters" class="mb-3">
          <TableProductFilters
            :sku.sync="sku"
            :short_description.sync="short_description"
            :description.sync="description"
            :url.sync="url"
            :tax.sync="tax"
            :ean13.sync="ean13"
            :width.sync="width"
            :height.sync="height"
            :depth.sync="depth"
            :weight.sync="weight"

            :buy_price.sync="buy_price"
            :min_buy_price.sync="min_buy_price"
            :max_buy_price.sync="max_buy_price"

            :special_price.sync="special_price"
            :min_special_price.sync="min_special_price"
            :max_special_price.sync="max_special_price"

            :recommended_retail_price.sync="recommended_retail_price"
            :min_recommended_retail_price.sync="min_recommended_retail_price"
            :max_recommended_retail_price.sync="max_recommended_retail_price"
            :wholesale_ean.sync="wholesale_ean"
            :wholesale_price.sync="wholesale_price"
            :min_wholesale_price.sync="min_wholesale_price"
            :max_wholesale_price.sync="max_wholesale_price"

            :wholesale_stock.sync="wholesale_stock"
            :min_wholesale_stock.sync="min_wholesale_stock"
            :max_wholesale_stock.sync="max_wholesale_stock"

            :product_stock.sync="product_stock"
            :min_product_stock.sync="min_product_stock"
            :max_product_stock.sync="max_product_stock"

            :supplier.sync="suppliers"
            :brand.sync="brands"
            :category.sync="categories"
          />
        </div>
      </transition>

      <div class="filter-wrapper d-flex justify-content-between align-items-center">
        <div class="d-flex responsive-flex responsive-flex--filters">
          <slot name="before-search-field" :showButton="showButton" :refetch="freshRefetch"></slot>

          <TableMainSearchField class="mr-2" v-model="search"/>
          <RefreshBtn
            :loading="isLoadingTableResult"
            @click.native="refresh"
            class="mr-2"
            style="margin-left: 0 !important;"
          />

          <slot name="after-refresh-button-field" :refetch="freshRefetch"></slot>
        </div>

        <div class="d-flex flex-row-reverse">
          <MultiplePageSizesSelection
            v-model="page_size"
            :available-values="availablePageSizeValues"
          />

        </div>
      </div>
      <transition
        @enter="enterTrans"
        @leave="leaveTrans"
      >
        <div v-if="online.length">
          <div class="mt-2">
            <span v-for="(item, index) in online"
              class="website-item align-items-center"
              :style="{color: productenColors[index]}"
              @click="configureWebsiteProductenColumns(item, index)"
            >
              <span>
                {{ item.name }}
              </span>

              <svg class="ml-1 remove-website" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 20 20" fill="currentColor" @click.stop="deselectWebsite(item.uid)">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>

              <div :style="{backgroundColor: productenColors[index]}" style="pointer-events: none;"></div>
            </span>
          </div>
        </div>
      </transition>
    </template>

    <slot :refetch="refetch"></slot>

    <template v-if="loaded" #row-action="props">
      <slot name="final-column-item" :refetch="refetch" v-bind="props"></slot>
    </template>

    <template v-for="(_, name) in $scopedSlots"
      slot-scope="slotData"
      :slot="name"

      v-if="name"
    >
      <slot
        :name="name"
        :refetch="refetch"

        v-bind="slotData"
      />
    </template>

    <template #cell-recommended_retail_price="{value}">
      <span>
        {{ value }}
      </span>
    </template>

    <template #cell-buy_price="{value}">
      <span>
        {{ value }}
      </span>
    </template>

    <template #cell-special_price="{value}">
      <span>
        {{ value }}
      </span>
    </template>

    <template #cell-wholesale_price="{value, item, extendedSlot, web_producten_uid}">
      <span>
        {{ value }}
      </span>
    </template>

    <template #cell-product_stock="{value}">
      <span>
        {{ value }}
      </span>
    </template>

    <template #cell-name="nameProps">
      <span>
        {{ nameProps.value }}
      </span>
    </template>
    <template
      v-for="field in editableFields"
      v-slot:[`cell-${field}`]="{value, item, extendedSlot, web_producten_uid}"
    >
      <EditableTableItem v-if="editableFields.includes(field)"
        :class="extendedSlot ? extendedSkeletonClass : skeletonClass"

        :value="value"
        @save="newValue => updateCatalogPosition(item.sku, field, newValue)"
      >
        {{ value }}
      </EditableTableItem>
    </template>

    <template #cell-active="{value, item, extendedSlot, web_producten_uid}">
      <span :class="extendedSlot ? extendedSkeletonClass : skeletonClass">
        <FormButtonCheckbox
          :value="value"
          :active="value"
          :removeCheckbox="true"
        >
          {{ value ? "Actief" : "Unactief" }}
        </FormButtonCheckbox>
      </span>
    </template>

    <template #cell-print="{value}">
      <span>
        {{ value }}
      </span>
    </template>

    <template #cell-brand="{value, item, extendedSlot}">
      <span :class="extendedSlot ? extendedSkeletonClass : skeletonClass">
        {{ value && value.name ? value.name : "---" }}
      </span>
    </template>

    <template #cell-ean13="{value}">
      <span>
        {{ value }}
      </span>
    </template>

    <template #cell-is_online="{value, item, extendedSlot, web_producten_uid}">
      <span :class="extendedSlot ? extendedSkeletonClass : skeletonClass">
        <FormButtonCheckbox :value="value" :active="value" :removeCheckbox="true" @click.native.stop.prevent="toggleWebsiteProducten(value, item.sku, web_producten_uid)">
          {{ value ? "online" : "offline" }}
        </FormButtonCheckbox>
      </span>
    </template>

    <template #cell-sku="{value, item, extendedSlot}">
      <span :class="extendedSlot ? extendedSkeletonClass : skeletonClass">
        {{ value }}
      </span>
    </template>

    <template v-for="(_, name) in nonUsedScopedSlots"
      slot-scope="slotData"
      :slot="name"
      v-if="name"
    >
      <slot
        :name="name"
        :refetch="refetch"

        v-bind="slotData"
      />
    </template>

    <template #before-row="{item}">
      <slot name="before-row" :item="item"></slot>
    </template>

  </TableTemplate>
</template>

<script>


import TableTemplate from 'src/layout/Table/TableTemplate'
import TableSearchField from "src/components/Inputs/TableSearchField.vue"
import TableActionDropdown from "src/components/Inputs/TableActionDropdown.vue"
import EmptyStatePlaceholder from "src/layout/EmptyStatePlaceholder.vue"
import FormButtonCheckbox from "src/components/Inputs/FormButtonCheckbox.vue"
import TableHeader from "./TableHeader.vue"
import EditablePrice from '../../../layout/EditTableItem/EditablePrice.vue'
import EditableTableItem from '../../../layout/EditTableItem/EditableTableItem.vue'
import EditableBarcode from '../../../layout/EditTableItem/EditableBarcode.vue'
import TableProductFilters from '../TableProductFilters.vue'
import RefreshBtn from '../../../layout/RefreshBtn.vue'

import MultiplePageSizesSelection from '../../../layout/Table/MultiplePageSizesSelection.vue'
import TableMainSearchField from '../../../layout/TableMainSearchField.vue'
import CheckboxOptions from '../../../layout/Table/DropdownCheckboxTableFilters/CheckboxOptions.vue'

import transitionFunctionConstructor from '../../../mixins/transitionFunctionConstructor'
import { productTypesOptions } from '../../../mixins/imageFileTypes'
import tableFieldOptions from "src/mixins/tableFieldOptions"
import selectMultipleItems from "src/mixins/selectMultipleItems"
import table from "src/mixins/table"
import keepScroll from "src/mixins/keepScroll"
import performanceChecker from "src/mixins/performanceChecker"

const websiteProductenDefaultColumns = [
  "Verkoopprijs", "Groothandel Prijs", "Aanbieding Prijs", "Status"
]

export default ({
  components: {
    RefreshBtn,
    TableProductFilters,
    TableTemplate,
    TableActionDropdown,
    EditablePrice,
    EditableBarcode,
    EditableTableItem,
    MultiplePageSizesSelection,
    TableHeader,
    EmptyStatePlaceholder,
    TableSearchField,
    FormButtonCheckbox,
    TableMainSearchField,
    CheckboxOptions,
  },
  props: {
    newlyCreatedAlertKey: String,
    title: {
      type: String,
      required: true,
    },
    extraParams: {
      type: Object,
      required: false,
    },
    tableLayoutId: {
      type: String,
      required: true,
    },
    hidePagination: {
      type: Boolean,
      required: false,
    },
    removeCard: {
      type: Boolean,
      required: false,
    },
    rawTable: {
      type: Boolean,
      required: false,
    },
    removeHeader: {
      type: Boolean,
      required: false,
    },
  },
  mixins: [
    transitionFunctionConstructor("enterTrans", "leaveTrans"),
    tableFieldOptions,
    selectMultipleItems,
    performanceChecker,
    keepScroll,

    table({

      pageReference: 'all-catalogs',

      minimumColumnSizes: {
        ean13: 290,
        print: 240,
      },
      defaultColumnSizes: {
        recommended_retail_price: 120,
        sku: 100,
        active: 100,
        tax: 100,

        wholesale_price: 115,
        buy_price: 115,
        special_price: 115,
        width: 115,
        height: 115,
        depth: 115,
        weight: 115,
        product_stock: 115,
      },

      columnTranslationMaps: {
        "SKU": "sku",
        "Naam": "name",
        "Korte beschrijving": "short_description",
        "Beschrijving": "description",
        "Volgorde": "order",
        "URL": "url",
        "Belasting": "tax",
        "Barcode": "ean13",
        "Breedte": "width",
        "Hoogte": "height",
        "Diepte": "depth",
        "Gewicht": "weight",
        "Actief": "active",
        "Voorraad": "product_stock",

        /*"Prijs excl. BTW": "price_excluding_taxes",
        "Prijs incl. BTW": "price_including_vat",*/
        "Groothandel Prijs": "wholesale_price",
        "Groothandel Voorraad": "wholesale_stock",
        "Aanbieding Prijs": "special_price",
        "Koop prijs": "buy_price",
        "Verkoopprijs": "recommended_retail_price",

        "Status": "is_online",

        "Print": "print",
      },
      disableColumnConfiguration: ["Status"],
      imageTypeOptions: productTypesOptions,
      getImageEndpoint: item => `/api/products/${item.sku}/files`,
      disableAlphabeticalSort: true,

      defaultColumns: [
        "Naam", "Volgorde", "Verkoopprijs", "Korte beschrijving", "SKU", "Actief", "Barcode",
      ],

      filterSelectOptions: { // Each key represents a filter, and its value is a function that returns the <select> options.
        "tax": vueInstance => vueInstance.taxOptionsAll,
      },
      page_size: 50,
      defaultSort: "name",

      params: {
        active: null,
        page_size: 50,
        name: "",
        search: "",
        sku: null,
        short_description: "",
        description: "",
        url: "",
        tax: "",
        ean13: "",
        wholesale_ean: "",
        width: "",
        height: "",
        depth: "",
        weight: "",

        buy_price: "",
        min_buy_price: "",
        max_buy_price: "",

        special_price: "",
        min_special_price: "",
        max_special_price: "",

        recommended_retail_price: "",
        min_recommended_retail_price: "",
        max_recommended_retail_price: "",

        wholesale_price: "",
        max_wholesale_price: "",
        min_wholesale_price: "",

        wholesale_stock: "",
        min_wholesale_stock: "",
        max_wholesale_stock: "",

        product_stock: "",
        min_product_stock: "",
        max_product_stock: "",

        suppliers: [],
        categories: [],
        brands: [],
      },
      columnConfigurationId: "catalog-table",

      getParams: (params, vm) => ({
        ...params,
        ...(vm.extraParams || {}),
      }),
      parseParamsBeforeSending: params => {
        if ((params.sort || "").split("---")[0] === "is_online") {
          params.online = [(params.sort || "").split("---")[1]]

          params.sort = "is_online"
        }
        /* if (params.category) {
          params.categories = [params.category]
        } */

        return params
      },

      isArrayQuery: ["categories", "suppliers", "brands"],
      routeTriggers: ["extraParams"],
      route: "/api/catalogs/products",
    }),
  ],
  data() {
    return {
      selectedWebsites: [],
      editableFields: ["order"],
      showFilters: false,

      selectingWebsitesToFocus: [],
      online: [],
      websiteProductenList: [],

      websiteProductenCurrentTableColumns: [
        websiteProductenDefaultColumns,
        websiteProductenDefaultColumns,
      ],

      focusedSkus: [],
      loadingWebsiteProducten: false,

      /**
       * [ Mixin keepScroll ] - localStorageKey: Unique key(one per page) that will be used as a reference to set/get the element's distance from the top
       */
      localStorageKey: "position_scroll_catalog"
    }
  },
  async created() {
    this.getActiveColumnsFromStorage(this.tableLayoutId)
    // if (this.online.length) this.queryFocusedWebsiteProducts()

    try {
      const websites = localStorage.getItem("online-selected-websites")
      if (websites) this.online = JSON.parse(websites) || []
    } catch (err) {
      this.online = []
    }

    if (this.online.length) {
      this.selectingWebsitesToFocus = this.online.slice()
      this.fetchWebsiteProducten()
      this.getProductenCustomOders()
    }
  },
  methods: {
    deselectWebsite(uid) {
      const index = this.online.findIndex(item => item.uid === uid)
      if (index > -1) {
        this.online.splice(index, 1)

        if (!this.online.length) {
          this.sort = "name"
          this.order = "asc"
        }

        this.$nextTick(() => this.selectingWebsitesToFocus = this.online.slice())
      }
    },
    getProductenCustomOders() {
      const newOrders = []
      this.websiteProductenCurrentTableColumns.map((item, index) => {
        let order

        try {
          order = localStorage.getItem("table-order:" + `website-producten-${index}`)
          order = order ? JSON.parse(order).filter(item => item) : websiteProductenDefaultColumns
        } catch (err) {
          order = websiteProductenDefaultColumns
        }

        newOrders.push(order)
      })
      this.websiteProductenCurrentTableColumns = newOrders
    },
    resetAllWebsiteColumnConfigurationLocalStorages() {
      Array.from({length: 50}).forEach(index => localStorage.setItem(`table-order:website-producten-${index}`, null))
    },
    configureWebsiteProductenColumns({ uid, name }, index) {
      this.$pushModal({
        modal: "CustomizeLayout",
        toBind: {
          id: `table-order:website-producten-${index}`,
          title: `Kolommen aanpassen - <b>${name}</b>`,

          columns: websiteProductenDefaultColumns.filter(col => this.currentTableColumns.includes(col)),
          defaultColumns: websiteProductenDefaultColumns,
          currentColumns: this.websiteProductenCurrentTableColumns[index].slice(),
          callback: activeValues => {
            this.websiteProductenCurrentTableColumns[index] = activeValues
            this.websiteProductenCurrentTableColumns = [...this.websiteProductenCurrentTableColumns]
            this.updateColumnOrder(null)
          },
        },
      })
    },

    async fetchWebsiteProducten() {
      this.loadingWebsiteProducten = true

      const websiteProductenList = {}
      await Promise.all(
        this.online.map(async ({uid, name}) => {
          websiteProductenList[uid] = (await this.$store.dispatch("cachedGet", {
            endpoint: `/api/websites/${uid}/products`,
            params: { params: {page_size: 10000} },
          })).data.results.map((p) => {p.recommended_retail_price = p.price; return p})
        })
      )
      this.websiteProductenList = websiteProductenList

      this.loadingWebsiteProducten = false
    },
    assignSelectionToFocusedWebsites(close) {
      this.online = this.selectingWebsitesToFocus.slice()

      this.resetAllWebsiteColumnConfigurationLocalStorages()
      if (!this.online.length) {
        this.sort = "name"
        this.order = "asc"
      }

      close && close()
      this.$nextTick(() => this.selectingWebsitesToFocus = this.online.slice())
    },
    handlePrint({ sku,ean13 }) {
      this.$handlePromise(async () => {
        const payload = { sku,ean13 }
        const input = document.getElementById(`print-${sku}`)
        const amount = input && input.value
        if (amount){ payload.quantity = parseInt(amount, 10)}else{  payload.quantity = 1;  }
        payload.status = 0
        await this.axios.post('/api/printjobs', payload)
        this.$success("Update succesvol!")
      })
    },

    toggleWebsiteProducten(online, sku, web_producten_uid) {
      this.$handlePromise(async () => {
        if (online) {
          await this.axios.delete(`/api/websites/${web_producten_uid}/products/${sku}`)
        } else {
          await this.axios.put(`/api/websites/${web_producten_uid}/products/${sku}`)
        }
        this.$store.dispatch('clearCacheSearch', "websites")
        this.$store.dispatch('clearCacheSearch', "products")

        this.fetchWebsiteProducten()
        this.$success("Succesvol geüpdatet websiteproduct!")
      })
    },
    async assignProductsToWebsite() {
      const selected = this.selected.slice()
      const websites = this.selectedWebsites.slice()

      this.$handlePromise(async () => {
        await Promise.all(
          selected.map(async sku => {
            const product = (await this.axios.get(`/api/products/${sku}`)).data
            await Promise.all(
              websites.map(async ({uid}) => {
                if (product.websites.includes(uid)) {
                  await this.axios.delete(`/api/websites/${uid}/products/${sku}`)
                } else {
                  await this.axios.put(`/api/websites/${uid}/products/${sku}`)
                }
                this.$store.dispatch('clearCacheSearch', "websites")
                this.$store.dispatch('clearCacheSearch', "products")
              })
            )
          })
        )

        this.$refs["assign-to-website-checkbox"].close()
        // this.online.length && this.queryFocusedWebsiteProducts()
        this.freshRefetch()
        this.$success("Succesvol bijgewerkte websiteproducten!")
      })
    },

    async updateProductWithMessage(sku, data) {
      const [field] = Object.keys(data)
      const [value] = Object.values(data)
      const message = "Product geüpdatet!"
      const payload = {...data}

      this.updateOneFieldLoad(sku, field)

      await this.updateFieldAction(sku, payload, '/api/products/')
      await this.refetchTableRow(sku, { field, newValue: value }, message)
    },
    async updateCatalogWithMessage(sku, data) {
      await this.updateSingleTableRowById(sku, data, "Catalog geüpdatet!")
    },
    async makeProductActive(item) {
      const { active, sku } = item;
      const payload = { active: !active }
      const message = "Product geüpdatet!"

      this.updateOneFieldLoad(sku, "active")

      await this.updateFieldAction(sku, payload, '/api/products/')
      await this.refetchTableRow(sku, { field: "active", newValue: !active }, message)
    },
    async makeWebProductActive(active, sku, web_producten_uid) {
      this.updateWebsiteProducten(sku, web_producten_uid, { active: !active })
    },
    async updateWebProductWithMessage(data, sku, web_producten_uid) {
      this.updateWebsiteProducten(sku, web_producten_uid, data)
    },

    async updateWebsiteProducten(sku, web_producten_uid, data) {
      if (await this.$handlePromise(
        this.axios.put(`/api/websites/${web_producten_uid}/products/${sku}`, data)
      )) {
        this.$success("Succesvol geüpdatet websiteproduct!")
        this.$store.dispatch('clearCacheSearch', "websites")
        this.$store.dispatch('clearCacheSearch', "products")
        this.fetchWebsiteProducten()
      }
    },

    customTranslationMap(translation) {
      const web = this.online.find(web => translation.includes(`">${web.name}</b>`))
      return web && `${this.columnTranslationMaps[translation.split("(")[0]]}---${web.uid}`
    },
    updateCatalogPosition(sku, field, newValue) {
      this.$handlePromise(async () => {
        await this.axios.put('/api/catalogs/products', { sku, [field]: newValue })
      })
      this.freshRefetch()
    }
  },
  computed: {
    extendedSkeletonClass() {
      return (this.loadingWebsiteProducten || this.isLoadingTableResult) ? "ske" : ""
    },
    extendedSlots() {
      return this.extendedTableColumnVariables.map(field => ({
        name: field.split("---")[0],
        slot: field,
        props: {
          web_producten_uid: field.split("---")[1],
        },
      }))
    },

    extendedTableColumnVariables() {
      return this.tableColumnVariables.filter(fieldName => fieldName !== undefined ? fieldName.includes("---") : undefined)
    },
    websiteProductenTableColumns() {
      const colors = this.productenColors
      const columns = this.currentTableColumns.slice()
      const websites = this.online
      const currentTableColumns = this.websiteProductenCurrentTableColumns

      columns.push(`Status`)
      websites.forEach((web, index) => {
        if (!currentTableColumns[index]) {
          currentTableColumns.push(websiteProductenDefaultColumns)
        }
        currentTableColumns[index].forEach(webProductColumn => {
          const baseColumnIndex = columns.findIndex(col => webProductColumn === col)
          if (baseColumnIndex > -1) {
            columns.splice(baseColumnIndex + index + 1, 0, `${columns[baseColumnIndex]}(<b style="color: ${colors[index]}">${web.name}</b>)`)
          }
        });
      })

      const removeMainStatusIndex = columns.findIndex(col => col === "Status")
      if (removeMainStatusIndex > - 1) columns.splice(removeMainStatusIndex, 1)

      return columns
    },

    reversedOnline() {
      return this.online.slice().reverse()
    },
    reversedColors() {
      return this.productenColors.slice().reverse()
    },
    productenColors() {
      return [
        "#16a34a",
        "#2563eb",
        "#dc2626",
        "#57534e",
        "#d97706",
      ]
    },

    disabledDragColumns() {
      return ["is_online"]
    },
    computedTitle() {
      return `${this.total} ${this.title}`
    },
    availablePageSizeValues() {
      return [10, 20, 50, 100, 200]
    },

    fieldsWithCommas() {
      return [
        "buy_price",
        "min_buy_price",
        "max_buy_price",

        "special_price",
        "min_special_price",
        "max_special_price",

        "recommended_retail_price",
        "min_recommended_retail_price",
        "max_recommended_retail_price",

        "wholesale_price",
        "max_wholesale_price",
        "min_wholesale_price",

        "product_stock",
        "min_product_stock",
        "max_product_stock",
      ]


    },
    mappeDefaultProductsFields() {
      let products = this.mappedProducts.slice()
      const websites = this.online
      if (!websites.length) return products

      const variables = this.extendedTableColumnVariables

      websites.forEach(({uid}) => {
        products.forEach((i, index) => {
          if (products[index][`is_online---${uid}`]) {
            variables.filter(field => field.includes(uid) && !field.includes("is_online")).forEach(extendedField => {
              if (products[index][extendedField] === undefined || products[index][extendedField] === null) {
                products[index][extendedField] = products[index][extendedField.split("---")[0]]
              }
            })
          }
        })
      })

      return products
    },
    mappedProducts() {
      let tableData = this.tableResult.map ? this.tableResult.map(item => ({
        ...item,
      })) : this.tableResult

      if (this.online.length) {
        const variables = this.extendedTableColumnVariables
        const websiteProductenList = this.websiteProductenList

        variables.forEach(field => {
          const website_uid = field.split("---")[1]
          const overwrittenProducts = websiteProductenList[website_uid]
          const baseField = field.split("---")[0]

          if (overwrittenProducts && overwrittenProducts.length) {
            tableData = tableData.map(row => {
              const overwritten = overwrittenProducts.find(product => row.sku === product.sku)
              row[field] = overwritten && overwritten[baseField] ? overwritten[baseField] : null
              row[`is_online---${website_uid}`] = overwritten ? true : false

              return row
            })
          } else {
            tableData = tableData.map(row => {
              row[`is_online---${website_uid}`] = false
              return row
            })
          }
        })
      }

      return tableData
    },

    activeColumns() {
      return this.online.length ? this.websiteProductenTableColumns : this.currentTableColumns
    },
    table() {
      return this.$refs.table
    },
    capitalize() {
      return [false, false, false, false]
    },
    nonUsedScopedSlots() {
      const usedSlots = ['header-buttons-before', 'header-buttons-after', 'name']

      const finalSlots = {...this.$scopedSlots}
      usedSlots.forEach(slot => delete finalSlots[slot])

      return finalSlots
    },
    /* Using computed property in order to not override mixin watch: {} */
    currentSort() {
      return this.sort
    },
  },
  watch: {
    currentSort(val) {
      if (val && val.includes("is_online")) {
        this.order = "desc"
      }
    },
    online(val) {
      this.updateColumnOrder(null)

      if (val.length) {
        localStorage.setItem("online-selected-websites", JSON.stringify(val))
      } else {
        localStorage.setItem("online-selected-websites", null)
      }

      this.freshRefetch()
      this.fetchWebsiteProducten()
    },

    selected(val) {
      this.$emit("input", [...val])
    },
    tableResult(val) {
      this.$emit("table-items", val)
    },
    showButton(val) {
      this.$emit("show-button", val)
    },
    result(val) {
      this.$emit("result", [...val])
    },
  },
})

</script>

<style>

.filter-wrapper .CheckboxOptions {
  font-size: 16px !important;
}

</style>

<style scoped>

.responsive-flex {
  flex-wrap: wrap;
}

.responsive-flex--filters {
  row-gap: 0.5rem;
}

@media (max-width: 800px) {
  .checkbox-options {
    margin-left: 0px !important;
  }

  .TableHeader {
    flex-wrap: wrap;
  }
}

.btn-grey {
  background: var(--black-40);
  color: var(--white);
  z-index: 10;
  position: relative;
}


input {
  width: 120px;
  padding-top: 6px;
  padding-bottom: 6px;
  padding-left: 6px;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
  transform: translate(2px, 0px);
}
.input-container {
  margin: 5px;
}


@media (max-width: 800px) {
  .filter-wrapper {
    flex-direction: column-reverse;
    align-items: initial;
  }

  .filter-wrapper > div {
    width: 100%;
  }
}

.select-website-checkbox-options {
  min-height: 40px;
}

.filter-button {
  background-color: #A7A9AB;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
  filter: brightness(1) !important;
}
.filter-button:hover {
  color: white !important;
  filter: brightness(1.08) !important;
}

.select-website {
  color: #999999;
}

.name-wrapper {
  color: currentColor;
}

.name-wrapper:hover {
  text-decoration: underline;
}

.button-icon {
  transform: translateY(-1px);
}

.drop {
  margin-top: -8px;
}

.products-counter {
  margin-left: 10px !important;
  cursor: initial !important;
  background-color: #f8f9fa !important;
  border: none !important;
}

.filter-icon, .counter {
  color: rgb(200,200,200);
}

.filter-icon {
  margin-bottom: 2px;
  margin-right: 1px;
}

.website-item {
  cursor: pointer;
  display: inline-flex;
  border-radius: 3px;
  padding: 6px 8px;
  position: relative;
  font-weight: 600;
  overflow: hidden;
  filter: brightness(1);
  transition: .15s;
}

.website-item:hover {
  filter: brightness(1.15);
}

.website-item + .website-item {
  margin-left: 7px;
}

.remove-website {
  display: block;
}

.remove-website:hover {
  color: #C7314A !important;
}

.website-item div {
  position: absolute;
  left: 0px;
  top: 0px;
  width: 100%;
  height: 100%;
  opacity: .15;
}
</style>
